import type {
  AccountNotifications,
  AlertSubscription,
  NotificationPreferences,
} from "@cloudserver/contracts";
import { BellRing, Inbox, Send } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAccountNotifications,
  useInbox,
  useLinkTelegram,
  useMarkInboxRead,
  useSavePreferences,
  useUnlinkTelegram,
} from "../../api/alerts.js";
import { useProjects } from "../../api/queries.js";
import { useServers } from "../../api/servers.js";
import { ErrorMessage, fieldError } from "../../ui/feedback.js";
import { relativeTime } from "../../ui/format.js";
import { PageHeader } from "../../ui/layout.js";
import { Badge, Button, Card, Select, Spinner, TextField } from "../../ui/primitives.js";
import { useToast } from "../../ui/toast.js";
import { TelegramLinkDialog } from "../alerts/link-dialog.js";
import { SeverityBadge } from "../alerts/list.js";
import { alertText } from "../alerts/text.js";

/*
 * «Настройки уведомлений» (меню профиля) (docs/alerts.md): the person links their Telegram
 * to the organisation's bots with a one-time deep link, chooses what to get
 * (projects or servers, severity), quiet hours, where (panel, Telegram) and
 * in which language; the latest in-panel notifications are below.
 */

const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

/** Time zones the browser knows; the person's own first. */
function timeZones(current: string): string[] {
  const all =
    (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.("timeZone") ??
    [];
  return [...new Set([current, browserZone(), "UTC", ...all])];
}

function TelegramCard({ data }: { data: AccountNotifications }) {
  const { t } = useTranslation();
  const [linking, setLinking] = useState<string | null>(null);
  // While the dialog is open the page looks for the chat the bot just got.
  useAccountNotifications(linking !== null);
  const link = useLinkTelegram();
  const unlink = useUnlinkTelegram();
  const open = (botId: string) => {
    setLinking(botId);
    link.mutate(botId);
  };
  const linkedNow = linking ? data.bots.find((b) => b.id === linking)?.chat : null;
  return (
    <Card className="flex flex-col gap-4">
      <header className="flex items-start gap-3">
        <Send className="mt-0.5 size-5 text-accent" aria-hidden />
        <div>
          <h2 className="font-semibold">{t("alerts.profile.telegramTitle")}</h2>
          <p className="text-sm text-muted">{t("alerts.profile.telegramIntro")}</p>
        </div>
      </header>
      {data.bots.length === 0 && <p className="text-sm text-muted">{t("alerts.profile.noBots")}</p>}
      <ul className="flex flex-col divide-y divide-line">
        {data.bots.map((bot) => (
          <li key={bot.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
            <span className="flex min-w-0 flex-col">
              <span className="flex items-center gap-2 font-medium">
                @{bot.username}
                {bot.isDefault && <Badge>{t("alerts.channels.default")}</Badge>}
              </span>
              <span className="text-sm text-muted">
                {bot.chat
                  ? t("alerts.profile.linkedAs", { chat: bot.chat.title })
                  : t("alerts.profile.notLinked")}
              </span>
            </span>
            {bot.chat ? (
              <Button
                variant="secondary"
                busy={unlink.isPending && unlink.variables === bot.chat.id}
                onClick={() => bot.chat && unlink.mutate(bot.chat.id)}
              >
                {t("alerts.profile.unlink")}
              </Button>
            ) : (
              <Button onClick={() => open(bot.id)}>{t("alerts.profile.link")}</Button>
            )}
          </li>
        ))}
      </ul>
      {linking && (
        <TelegramLinkDialog
          title={
            linkedNow
              ? t("alerts.link.personDone", { chat: linkedNow.title })
              : t("alerts.link.personTitle")
          }
          steps={[t("alerts.link.personStep1"), t("alerts.link.personStep2")]}
          link={link.data}
          error={link.error ? <ErrorMessage error={link.error} /> : null}
          onClose={() => {
            setLinking(null);
            link.reset();
          }}
        />
      )}
    </Card>
  );
}

/** One followed project: every server, or the ones ticked. */
function ProjectServers({
  projectId,
  subscription,
  onChange,
}: {
  projectId: string;
  subscription: AlertSubscription;
  onChange: (next: AlertSubscription) => void;
}) {
  const { t } = useTranslation();
  const servers = useServers(projectId);
  const chosen = subscription.serverIds;
  return (
    <div className="ml-6 flex flex-col gap-1.5">
      <Select
        label={t("alerts.profile.serversOf")}
        value={chosen === null ? "all" : "chosen"}
        onChange={(e) => onChange({ projectId, serverIds: e.target.value === "all" ? null : [] })}
        className="self-start"
      >
        <option value="all">{t("alerts.profile.allServers")}</option>
        <option value="chosen">{t("alerts.profile.chosenServers")}</option>
      </Select>
      {chosen !== null &&
        servers.data?.map((s) => (
          <label key={s.id} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="accent-accent"
              checked={chosen.includes(s.id)}
              onChange={(e) =>
                onChange({
                  projectId,
                  serverIds: e.target.checked
                    ? [...chosen, s.id]
                    : chosen.filter((id) => id !== s.id),
                })
              }
            />
            {s.name}
          </label>
        ))}
    </div>
  );
}

function PreferencesCard({ data }: { data: AccountNotifications }) {
  const { t } = useTranslation();
  const projects = useProjects();
  const save = useSavePreferences();
  const toast = useToast();
  const [draft, setDraft] = useState<NotificationPreferences>(data.preferences);
  const quiet = draft.quietHours;
  const subscriptions = draft.projects;
  const toggleProject = (projectId: string, on: boolean) =>
    setDraft({
      ...draft,
      projects: on
        ? [...(subscriptions ?? []), { projectId, serverIds: null }]
        : (subscriptions ?? []).filter((s) => s.projectId !== projectId),
    });
  const replace = (next: AlertSubscription) =>
    setDraft({
      ...draft,
      projects: (subscriptions ?? []).map((s) => (s.projectId === next.projectId ? next : s)),
    });
  return (
    <Card className="flex flex-col gap-6">
      <header className="flex items-start gap-3">
        <BellRing className="mt-0.5 size-5 text-accent" aria-hidden />
        <div>
          <h2 className="font-semibold">{t("alerts.profile.whatTitle")}</h2>
          <p className="text-sm text-muted">{t("alerts.profile.whatIntro")}</p>
        </div>
      </header>

      <fieldset className="flex flex-col gap-2">
        <legend className="pb-1 text-sm font-medium">{t("alerts.profile.projects")}</legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="projects"
            className="accent-accent"
            checked={subscriptions === null}
            onChange={() => setDraft({ ...draft, projects: null })}
          />
          {t("alerts.profile.allProjects")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="projects"
            className="accent-accent"
            checked={subscriptions !== null}
            onChange={() => setDraft({ ...draft, projects: subscriptions ?? [] })}
          />
          {t("alerts.profile.chosenProjects")}
        </label>
        {subscriptions !== null && (
          <div className="ml-6 flex flex-col gap-2">
            {projects.data?.map((p) => {
              const subscription = subscriptions.find((s) => s.projectId === p.id);
              return (
                <div key={p.id} className="flex flex-col gap-1.5">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="accent-accent"
                      checked={subscription !== undefined}
                      onChange={(e) => toggleProject(p.id, e.target.checked)}
                    />
                    {p.name}
                  </label>
                  {subscription && (
                    <ProjectServers
                      projectId={p.id}
                      subscription={subscription}
                      onChange={replace}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </fieldset>

      <label className="flex max-w-sm flex-col gap-1.5 text-sm font-medium">
        {t("alerts.profile.minSeverity")}
        <Select
          label={t("alerts.profile.minSeverity")}
          value={draft.minSeverity}
          onChange={(e) =>
            setDraft({ ...draft, minSeverity: e.target.value === "critical" ? "critical" : "warn" })
          }
        >
          <option value="warn">{t("alerts.minSeverity.warn")}</option>
          <option value="critical">{t("alerts.minSeverity.critical")}</option>
        </Select>
      </label>

      <fieldset className="flex flex-col gap-3">
        <legend className="pb-1 text-sm font-medium">{t("alerts.profile.quietTitle")}</legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-accent"
            checked={quiet !== null}
            onChange={(e) =>
              setDraft({
                ...draft,
                quietHours: e.target.checked
                  ? { start: "23:00", end: "08:00", timeZone: browserZone(), allowCritical: true }
                  : null,
              })
            }
          />
          {t("alerts.profile.quietOn")}
        </label>
        {quiet && (
          <div className="ml-6 flex flex-col gap-3">
            <div className="grid max-w-md gap-3 sm:grid-cols-2">
              <TextField
                label={t("alerts.profile.quietStart")}
                type="time"
                value={quiet.start}
                onChange={(e) =>
                  setDraft({ ...draft, quietHours: { ...quiet, start: e.target.value } })
                }
              />
              <TextField
                label={t("alerts.profile.quietEnd")}
                type="time"
                value={quiet.end}
                onChange={(e) =>
                  setDraft({ ...draft, quietHours: { ...quiet, end: e.target.value } })
                }
              />
            </div>
            <label className="flex max-w-md flex-col gap-1.5 text-sm font-medium">
              {t("alerts.profile.timeZone")}
              <Select
                label={t("alerts.profile.timeZone")}
                value={quiet.timeZone}
                onChange={(e) =>
                  setDraft({ ...draft, quietHours: { ...quiet, timeZone: e.target.value } })
                }
              >
                {timeZones(quiet.timeZone).map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </Select>
            </label>
            {fieldError(save.error, "quietHours", t) && (
              <p className="text-xs text-danger">{fieldError(save.error, "quietHours", t)}</p>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="accent-accent"
                checked={quiet.allowCritical}
                onChange={(e) =>
                  setDraft({ ...draft, quietHours: { ...quiet, allowCritical: e.target.checked } })
                }
              />
              {t("alerts.profile.allowCritical")}
            </label>
          </div>
        )}
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="pb-1 text-sm font-medium">{t("alerts.profile.whereTitle")}</legend>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-accent"
            checked={draft.panel}
            onChange={(e) => setDraft({ ...draft, panel: e.target.checked })}
          />
          {t("alerts.profile.panel")}
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-accent"
            checked={draft.telegram}
            onChange={(e) => setDraft({ ...draft, telegram: e.target.checked })}
          />
          {t("alerts.profile.telegram")}
        </label>
      </fieldset>

      <label className="flex max-w-sm flex-col gap-1.5 text-sm font-medium">
        {t("alerts.profile.language")}
        <Select
          label={t("alerts.profile.language")}
          value={draft.language}
          onChange={(e) => setDraft({ ...draft, language: e.target.value === "ru" ? "ru" : "en" })}
        >
          <option value="ru">Русский</option>
          <option value="en">English</option>
        </Select>
      </label>

      <ErrorMessage error={save.error} />
      <div>
        <Button
          busy={save.isPending}
          onClick={() =>
            save.mutate(draft, {
              onSuccess: () => toast({ tone: "ok", title: t("alerts.profile.saved") }),
            })
          }
        >
          {t("alerts.profile.save")}
        </Button>
      </div>
    </Card>
  );
}

function InboxCard() {
  const { t, i18n } = useTranslation();
  const inbox = useInbox();
  const read = useMarkInboxRead();
  const items = inbox.data?.items ?? [];
  return (
    <Card className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Inbox className="mt-0.5 size-5 text-accent" aria-hidden />
          <div>
            <h2 className="font-semibold">{t("alerts.profile.inboxTitle")}</h2>
            <p className="text-sm text-muted">{t("alerts.profile.inboxIntro")}</p>
          </div>
        </div>
        {(inbox.data?.unread ?? 0) > 0 && (
          <Button variant="secondary" busy={read.isPending} onClick={() => read.mutate(undefined)}>
            {t("alerts.actions.markRead")}
          </Button>
        )}
      </header>
      {inbox.isPending && <Spinner label={t("common.loading")} />}
      {inbox.data && items.length === 0 && (
        <p className="text-sm text-muted">{t("alerts.profile.inboxEmpty")}</p>
      )}
      <ul className="flex flex-col divide-y divide-line">
        {items.map((item) => (
          <li key={item.id} className="flex flex-wrap items-start gap-2 py-2.5">
            <span
              className={`mt-1.5 size-2 shrink-0 rounded-full ${item.readAt ? "bg-transparent" : "bg-accent"}`}
              aria-hidden
            />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-2 text-sm">
                {item.phase === "resolved" ? (
                  <Badge tone="ok">{t("alerts.status.resolved")}</Badge>
                ) : (
                  <SeverityBadge alert={item.alert} />
                )}
                <span className={item.readAt ? "" : "font-medium"}>
                  {item.alert.serverName ? `${item.alert.serverName}: ` : ""}
                  {alertText(t, item.alert, i18n.language)}
                </span>
              </span>
              <span className="text-xs text-muted">
                {!item.readAt && <span className="sr-only">{t("alerts.profile.unread")} · </span>}
                {item.alert.projectName} · {relativeTime(item.createdAt, i18n.language)}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <ErrorMessage error={inbox.error} />
    </Card>
  );
}

export function NotificationSettingsPage() {
  const { t } = useTranslation();
  const account = useAccountNotifications();
  return (
    <>
      <PageHeader
        title={t("alerts.profile.title")}
        intro={t("alerts.profile.intro")}
        help="alerts"
      />
      {account.isPending && <Spinner label={t("common.loading")} />}
      <ErrorMessage error={account.error} />
      {account.data && (
        <>
          <TelegramCard data={account.data} />
          {/* A new form when the saved settings change elsewhere (another tab). */}
          <PreferencesCard key={JSON.stringify(account.data.preferences)} data={account.data} />
        </>
      )}
      <InboxCard />
    </>
  );
}
